import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr, API } from "../../lib/api";
import { toast } from "sonner";
import { Gift, FileText, Check, RotateCcw } from "lucide-react";

const STATUS = {
  active: { label: "Actif", cls: "bg-emerald-50 text-emerald-700" },
  used: { label: "Utilisé", cls: "bg-ink-400/10 text-ink-500" },
  expired: { label: "Expiré", cls: "bg-amber-50 text-amber-700" },
};

export default function GiftCardsTab() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/giftcards").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (gc) => {
    try {
      const { data } = await api.post(`/admin/giftcards/${gc.id}/redeem`, {});
      toast.success(data.status === "used" ? "Marqué comme utilisé" : "Réactivé");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const totalActive = rows.filter((r) => r.status === "active").reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <div data-testid="giftcards-tab">
      <div className="rounded-2xl bg-brand-50/60 border border-brand/15 p-5 mb-6 text-sm text-ink-600 flex items-center gap-3">
        <Gift size={20} className="text-brand" />
        <span>Chèques cadeaux vendus. <strong>{rows.length}</strong> au total · <strong>{totalActive} €</strong> encore actifs (non utilisés). Marquez un chèque « utilisé » lorsqu'il est déduit d'un séjour.</span>
      </div>

      <div className="space-y-3">
        {rows.length === 0 && <p className="text-ink-400 text-sm">Aucun chèque cadeau vendu pour le moment.</p>}
        {rows.map((gc) => {
          const st = STATUS[gc.status] || STATUS.active;
          return (
            <div key={gc.id} className="rounded-2xl bg-white border border-black/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`giftcard-${gc.code}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-semibold text-ink-900">{gc.code}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                  <span className="font-heading text-lg text-brand">{gc.amount} €</span>
                </div>
                <p className="text-xs text-ink-400 mt-1">
                  De {gc.buyer_name || "—"}{gc.recipient_name ? ` · pour ${gc.recipient_name}` : ""}
                  {gc.expires_at ? ` · valable jusqu'au ${gc.expires_at}` : ""}
                  {gc.used_at ? ` · utilisé le ${gc.used_at.slice(0, 10)}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <a href={`${API}/giftcards/pdf/${gc.code}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-ink-400/25 text-ink-700 px-3 py-1.5 text-sm hover:bg-black/5" data-testid={`giftcard-pdf-${gc.code}`}>
                  <FileText size={14} /> PDF
                </a>
                <button onClick={() => toggle(gc)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium ${gc.status === "used" ? "border border-ink-400/25 text-ink-700 hover:bg-black/5" : "bg-brand text-white hover:bg-brand-600"}`} data-testid={`giftcard-toggle-${gc.code}`}>
                  {gc.status === "used" ? <><RotateCcw size={14} /> Réactiver</> : <><Check size={14} /> Marquer utilisé</>}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
